import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { _Api } from '../../common/api/_call';
import CatalogAllFilterPanel from './CatalogAllFilterPanel';
import {
  CATALOG_COLLECTION_NAV_ID,
  CATALOG_ITEM_NAV_ID,
  CATALOG_STYLE_NAV_ID,
} from '../CatalogNavbar';
import {
  DEFAULT_CATALOG_FILTERS,
  catalogFilterType,
  countCatalogFilterSelections,
  createCatalogFiltersForSubCategory,
  getFilterCountExcludeKeys,
  reconcileFiltersForInventoryMode,
} from './catalogAllFilterUtils';
import newCatalogGridIcon from '../../../assets/images/newcataloggridicon.svg';
import newCatalogFilterIcon from '../../../assets/images/newcatalogfiltericon.svg';
import newCatalogSearchIcon from '../../../assets/images/newcatalogsearchicon.svg';
import newCatalogSearchClearIcon from '../../../assets/images/newcatalogsearchclearicon.svg';
import tableIconSvg from '../../../assets/images/tableiconsvg.svg';
import ExportOptionModal from '../../../components/common/ExportOptionModal';

const ALL_DROPDOWN_ID = '__catalog_all__';

const sortOptionsByName = (list) =>
  [...(list || [])].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', undefined, {
      sensitivity: 'base',
    })
  );

const UploadIcon = () => (
  <svg
    className="catalog-all-toolbar-icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 4.99996L11.6464 4.64641L12 4.29285L12.3536 4.64641L12 4.99996ZM12.5 14C12.5 14.2761 12.2761 14.5 12 14.5C11.7239 14.5 11.5 14.2761 11.5 14H12.5ZM6.64648 9.64641L11.6464 4.64641L12.3536 5.35351L7.35358 10.3536L6.64648 9.64641ZM12.3536 4.64641L17.3536 9.64641L16.6464 10.3536L11.6464 5.35351L12.3536 4.64641ZM12.5 4.99996V14H11.5V4.99996H12.5Z"
      fill="#9E9E9E"
    />
    <path
      d="M5 16V17C5 18.1046 5.89543 19 7 19H17C18.1046 19 19 18.1046 19 17V16"
      stroke="#9E9E9E"
    />
  </svg>
);

const searchResultRowContent = (result) => (
  <>
    <div className="pos-search-result-thumb">
      {result?.main_image ? (
        <img
          src={result.main_image}
          alt=""
          onError={(e) => {
            e.target.onerror = null;
            e.target.src =
              'https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png';
          }}
        />
      ) : null}
    </div>
    <div className="pos-search-result-text">
      <div className="pos-search-result-name">{result?.name}</div>
      <div className="pos-search-result-meta">
        {result?.SKU}
        {result?.stock_id ? ' - ' + result.stock_id : ''}
      </div>
    </div>
  </>
);

/**
 * Catalog toolbar + filter panel for Item / Collection / Style product pages.
 */
const CatalogProductsToolbarSection = ({
  isCrown,
  onCrownClick,
  categoryId,
  subCategoryId,
  subCategoryName = '',
  customer,
  onSetProductData,
  appliedCatalogFilters,
  setAppliedCatalogFilters,
  isListView,
  setIsListView,
}) => {
  const { t } = useTranslation();
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchlist, setSearchlist] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [draftCatalogFilters, setDraftCatalogFilters] = useState(
    DEFAULT_CATALOG_FILTERS
  );
  const [catalogFilterData, setCatalogFilterData] = useState(null);
  const [catalogFilterPriceBounds, setCatalogFilterPriceBounds] = useState(null);
  const [styleSubCategories, setStyleSubCategories] = useState([]);
  const catalogFilterFetchTypeRef = useRef(null);
  const searchInputRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const searchDebounceRef = useRef(null);
  const optionDetails = isCrown;

  useEffect(() => {
    const filterType = catalogFilterType(optionDetails);
    if (catalogFilterFetchTypeRef.current === filterType) return;

    setCatalogFilterData(null);
    setCatalogFilterPriceBounds(null);

    let active = true;
    const loadFilters = async () => {
      try {
        const res = await _Api({ type: filterType }, 'api/v1/POS/catalog/filters');
        if (!active) return;
        catalogFilterFetchTypeRef.current = filterType;
        if (res?.code === 200 && res?.data) {
          setCatalogFilterData(res.data);
          setCatalogFilterPriceBounds(res.data.Price ?? null);
        } else {
          setCatalogFilterData(null);
          setCatalogFilterPriceBounds(null);
        }
      } catch (err) {
        console.log(err, 'err');
        if (active) {
          catalogFilterFetchTypeRef.current = filterType;
          setCatalogFilterData(null);
          setCatalogFilterPriceBounds(null);
        }
      }
    };
    loadFilters();
    return () => {
      active = false;
    };
  }, [optionDetails]);

  useEffect(() => {
    if (categoryId !== CATALOG_STYLE_NAV_ID) {
      setStyleSubCategories([]);
      return undefined;
    }

    let active = true;
    const loadStyleSubCategories = async () => {
      try {
        const res = await _Api(
          {
            category_id: CATALOG_STYLE_NAV_ID,
            type: optionDetails ? 'catalog' : 'inventory',
          },
          'api/v1/POS/common/getSubCategoury'
        );
        if (!active) return;
        setStyleSubCategories(
          res?.code === 200 && res?.data?.length ? res.data : []
        );
      } catch (err) {
        console.log(err, 'err');
        if (active) setStyleSubCategories([]);
      }
    };
    loadStyleSubCategories();
    return () => {
      active = false;
    };
  }, [categoryId, optionDetails]);

  useEffect(() => {
    if (optionDetails || !catalogFilterData) return;
    if (
      categoryId !== CATALOG_ITEM_NAV_ID &&
      categoryId !== CATALOG_COLLECTION_NAV_ID
    ) {
      return;
    }

    const sectionKey =
      categoryId === CATALOG_ITEM_NAV_ID ? 'item' : 'collection';
    const next = reconcileFiltersForInventoryMode(
      appliedCatalogFilters,
      catalogFilterData,
      categoryId
    );
    const prevSelected = appliedCatalogFilters[sectionKey] || [];
    const nextSelected = next[sectionKey] || [];
    if (
      prevSelected.length === nextSelected.length &&
      prevSelected.every((id, index) => id === nextSelected[index])
    ) {
      return;
    }

    setAppliedCatalogFilters(next);
    setDraftCatalogFilters(next);
  }, [
    optionDetails,
    catalogFilterData,
    categoryId,
    appliedCatalogFilters,
    setAppliedCatalogFilters,
  ]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!categoryDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdownOpen]);

  const getsearchcatalog = async (search) => {
    try {
      const postdata = {
        type: optionDetails ? 'catalog' : 'inventory',
        limit: 0,
        skip: 0,
        sale: 'top_sale',
        search: search || '',
        customer_id: customer?.id,
        category_id: categoryId || '',
      };
      const res = await _Api(postdata, 'api/v1/POS/home/search');
      setSearchlist(res?.code === 200 ? res?.data : []);
    } catch (err) {
      console.log(err, 'err');
    }
  };
  // const handleSearchChange = (value) => {
  //   setSearchQuery(value);
  //   if (value?.trim()) {
  //     getsearchcatalog(value);
  //   } else {
  //     setSearchlist([]);
  //   }
  // };

  const handleSearchChange = (value) => {
    setSearchQuery(value);

    if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
    }

    if (!value.trim()) {
      setSearchlist([]);
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
        getsearchcatalog(value);
    }, 300);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchlist([]);
  };

  const downloadCatalogPdf = async (kind = 'catalog') => {
    try {
      setDownloading(true);
      const customerdata = localStorage.getItem('addcustomerdtaa');
      const parsedCustomer = customerdata ? JSON.parse(customerdata) : null;
      // Do not block PDF export when customer address is missing.
      // Backend fills store/location address for any empty customer fields.
      const selectedSectionIds = getSelectedSectionIds();
      const selectedItemId =
        selectedSectionIds.length === 1 ? selectedSectionIds[0] : null;
      const postdata = {
        Item_id: isAllSectionItemsSelected()
          ? 'All'
          : selectedItemId || subCategoryId || '',
        type: optionDetails ? 'catalog' : 'inventory',
        category_id: categoryId || '',
        customer_id: customer?.id ? customer.id : '',
        shippingInfo: parsedCustomer || customer || '',
        website_url: window.location.hostname,
      };
      // const res = await _Api(postdata, 'api/v1/POS/customOrder/getCatalogPdf');
      const res = await _Api(
        postdata,
        kind === 'detail'
          ? 'api/v1/POS/customOrder/productDetail'
          : 'api/v1/POS/customOrder/getCatalogPdf',
      );
      if (res.code === 200) {
        window.open(res?.url, '_blank');
        toast.success(res?.message || 'PDF generated successfully');
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (err) {
      console.log(err, 'err');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    return () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
    };
}, []);

  const filterCountExcludeKeys = getFilterCountExcludeKeys(categoryId);

  const activeFilterCount = countCatalogFilterSelections(appliedCatalogFilters, {
    priceBounds: catalogFilterPriceBounds,
    excludeKeys: filterCountExcludeKeys,
  });

  const openFilterPanel = () => {
    setDraftCatalogFilters({ ...appliedCatalogFilters });
    setFilterOpen(true);
  };

  const handleApplyFilters = (filters) => {
    setAppliedCatalogFilters(filters);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    const maxPrice =
      catalogFilterPriceBounds?.max ?? draftCatalogFilters.price?.[1];
    const cleared = {
      ...createCatalogFiltersForSubCategory(categoryId, subCategoryId),
      ...(maxPrice != null ? { price: [0, maxPrice] } : {}),
    };
    setDraftCatalogFilters(cleared);
    setAppliedCatalogFilters(cleared);
    setFilterOpen(false);
  };

  const defaultSectionSelections = {};
  if (categoryId === CATALOG_ITEM_NAV_ID && subCategoryId) {
    defaultSectionSelections.item = [subCategoryId];
  } else if (categoryId === CATALOG_COLLECTION_NAV_ID && subCategoryId) {
    defaultSectionSelections.collection = [subCategoryId];
  } else if (categoryId === CATALOG_STYLE_NAV_ID && subCategoryId) {
    defaultSectionSelections.style = [subCategoryId];
  }

  const getFilterSectionKey = () => {
    if (categoryId === CATALOG_ITEM_NAV_ID) return 'item';
    if (categoryId === CATALOG_COLLECTION_NAV_ID) return 'collection';
    if (categoryId === CATALOG_STYLE_NAV_ID) return 'style';
    return null;
  };

  const getSectionOptions = () => {
    const sectionKey = getFilterSectionKey();
    if (!sectionKey) return [];
    if (sectionKey === 'style') {
      return sortOptionsByName(styleSubCategories);
    }
    return sortOptionsByName(catalogFilterData?.[sectionKey]);
  };

  const getSelectedSectionIds = () => {
    const sectionKey = getFilterSectionKey();
    if (!sectionKey) return [];
    return appliedCatalogFilters[sectionKey] || [];
  };

  const isAllSectionItemsSelected = () => {
    const options = getSectionOptions();
    const selected = getSelectedSectionIds();
    if (!options.length || !selected.length) return false;
    return selected.length > 1 || selected.length === options.length;
  };

  const getDropdownToggleLabel = () => {
    if (isAllSectionItemsSelected()) return t('All');
    const selected = getSelectedSectionIds();
    const options = getSectionOptions();
    if (selected.length === 1) {
      const match = options.find((entry) => entry._id === selected[0]);
      if (match?.name) return match.name;
      if (selected[0] === subCategoryId && subCategoryName) return subCategoryName;
    }
    if (subCategoryName) return subCategoryName;
    return t('All');
  };

  const applySectionSelection = (nextIds) => {
    const sectionKey = getFilterSectionKey();
    if (!sectionKey) return;
    const next = {
      ...appliedCatalogFilters,
      [sectionKey]: nextIds,
    };
    setAppliedCatalogFilters(next);
    setDraftCatalogFilters(next);
  };

  const handleCategoryDropdownOptionClick = (optionId) => {
    if (optionId === ALL_DROPDOWN_ID) {
      const allIds = getSectionOptions()
        .map((entry) => entry._id)
        .filter(Boolean);
      applySectionSelection(allIds);
    } else {
      applySectionSelection([optionId]);
    }
    setCategoryDropdownOpen(false);
  };

  const renderCategoryDropdown = () => {
    const sectionOptions = getSectionOptions();
    const sectionKey = getFilterSectionKey();
    if (!sectionKey || !sectionOptions.length) return null;

    const selectedIds = getSelectedSectionIds();
    const showAllSelected = isAllSectionItemsSelected();
    const toggleLabel = getDropdownToggleLabel();
    const dropdownId = `catalogCategoryDropdown-${categoryId}`;

    return (
      <div
        ref={categoryDropdownRef}
        className="catalog-all-item-dropdown dropdown"
      >
        <button
          type="button"
          className={`catalog-all-item-dropdown-toggle dropdown-toggle${
            categoryDropdownOpen ? ' show' : ''
          }`}
          id={dropdownId}
          aria-expanded={categoryDropdownOpen}
          onClick={() => setCategoryDropdownOpen((open) => !open)}
          title={toggleLabel}
        >
          <span className="catalog-all-item-dropdown-label catalog-all-item-dropdown-label--single">
            {toggleLabel}
          </span>
          <span className="catalog-all-item-dropdown-chevron" aria-hidden />
        </button>
        <ul
          className={
            categoryDropdownOpen
              ? 'dropdown-menu catalog-all-item-dropdown-menu show'
              : 'dropdown-menu catalog-all-item-dropdown-menu'
          }
          aria-labelledby={dropdownId}
        >
          <li key={ALL_DROPDOWN_ID}>
            <button
              type="button"
              className={
                showAllSelected ? 'dropdown-item active' : 'dropdown-item'
              }
              onClick={() => handleCategoryDropdownOptionClick(ALL_DROPDOWN_ID)}
            >
              {t('All')}
            </button>
          </li>
          {sectionOptions.map((item) => {
            const isActive =
              !showAllSelected &&
              selectedIds.length === 1 &&
              selectedIds[0] === item._id;
            return (
              <li key={item._id}>
                <button
                  type="button"
                  className={isActive ? 'dropdown-item active' : 'dropdown-item'}
                  onClick={() => handleCategoryDropdownOptionClick(item._id)}
                >
                  {item.name}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderCrownToggle = () => (
    <button
      type="button"
      className={
        isCrown
          ? 'catalog-all-toolbar-left-btn is-catalog'
          : 'catalog-all-toolbar-left-btn is-inventory'
      }
      onClick={onCrownClick}
      aria-label={isCrown ? t('Catalog view') : t('Inventory view')}
    >
      <img
        src={
          isCrown
            ? base_assets + 'images/pos/icons/crown-outline.png'
            : base_assets + 'images/pos/icons/warehouse.png'
        }
        alt=""
      />
    </button>
  );

  const renderToolbarLeading = () => (
    <div className="catalog-all-toolbar-leading">{renderCategoryDropdown()}</div>
  );

  return (
    <>
      <CatalogAllFilterPanel
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        draft={draftCatalogFilters}
        onDraftChange={setDraftCatalogFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        filterData={catalogFilterData}
        defaultSectionSelections={defaultSectionSelections}
        categoryId={categoryId}
      />
      <div
        className={
          searchOpen
            ? 'catalog-all-toolbar catalog-all-toolbar--search-open'
            : 'catalog-all-toolbar'
        }
      >
        {renderToolbarLeading()}

        {searchOpen ? (
          <>
            <div className="catalog-all-toolbar-spacer" aria-hidden="true" />
            <div className="catalog-all-toolbar-right">
              {renderCrownToggle()}
              <button
                type="button"
                className={
                  isListView
                    ? 'catalog-all-toolbar-action is-active'
                    : 'catalog-all-toolbar-action'
                }
                onClick={() => setIsListView((v) => !v)}
                aria-label={
                  isListView ? t('Toggle grid view') : t('Toggle table view')
                }
              >
                <img
                  className="catalog-all-toolbar-icon"
                  src={isListView ? tableIconSvg : newCatalogGridIcon}
                  alt=""
                />
              </button>
              <button
                type="button"
                className="catalog-all-toolbar-action catalog-all-toolbar-filter-btn"
                onClick={openFilterPanel}
                aria-label={t('Filter')}
              >
                <img
                  className="catalog-all-toolbar-icon"
                  src={newCatalogFilterIcon}
                  alt=""
                />
                {activeFilterCount > 0 ? (
                  <span className="catalog-all-toolbar-filter-badge">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              <div className="catalog-all-toolbar-search-wrap">
                <div className="com-search-area catalog-all-search-bar-input">
                  <img
                    className="catalog-all-search-bar-icon"
                    src={newCatalogSearchIcon}
                    alt=""
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    // onChange={(e) => handleSearchChange(e.target.value)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      if (searchDebounceRef.current) {
                        clearTimeout(searchDebounceRef.current);
                      }
                      searchDebounceRef.current = setTimeout(() => {
                        getsearchcatalog(value);
                      }, 300);
                    }}
                    placeholder={t('Search SKU, Stock ID, Product name')}
                    autoComplete="off"
                    aria-label={t('Search SKU, Stock ID, Product name')}
                  />
                  <button
                    type="button"
                    className="catalog-all-search-bar-clear"
                    onClick={closeSearch}
                    aria-label={t('Close search')}
                  >
                    <img
                      className="catalog-all-search-bar-clear-icon"
                      src={newCatalogSearchClearIcon}
                      alt=""
                    />
                  </button>
                </div>
                {searchlist?.length ? (
                  <ul className="dropdown-menu custom-search-dropdown catalog-all-search-dropdown show">
                    {searchlist.map((result, key) => (
                      <Link
                        to="/app/pos/products/ProductDetails"
                        className="link-style"
                        onClick={() =>
                          onSetProductData?.(
                            result?._id,
                            result?.name,
                            result?.Item
                          )
                        }
                        key={key}
                      >
                        <li className="dropdown-item pos-search-result-item">
                          {searchResultRowContent(result)}
                        </li>
                      </Link>
                    ))}
                  </ul>
                ) : null}
              </div>
              <button
                type="button"
                className="catalog-all-toolbar-action"
                onClick={() => !downloading && setExportModalOpen(true)}
                disabled={downloading}
                aria-label={t('Export catalog')}
              >
                <UploadIcon />
              </button>
            </div>
          </>
        ) : (
          <div className="catalog-all-toolbar-actions catalog-all-toolbar-actions--default">
            {renderCrownToggle()}
            <button
              type="button"
              className={
                isListView
                  ? 'catalog-all-toolbar-action is-active'
                  : 'catalog-all-toolbar-action'
              }
              onClick={() => setIsListView((v) => !v)}
              aria-label={
                isListView ? t('Toggle grid view') : t('Toggle table view')
              }
            >
              <img
                className="catalog-all-toolbar-icon"
                src={isListView ? tableIconSvg : newCatalogGridIcon}
                alt=""
              />
            </button>
            <button
              type="button"
              className="catalog-all-toolbar-action catalog-all-toolbar-filter-btn"
              onClick={openFilterPanel}
              aria-label={t('Filter')}
            >
              <img
                className="catalog-all-toolbar-icon"
                src={newCatalogFilterIcon}
                alt=""
              />
              {activeFilterCount > 0 ? (
                <span className="catalog-all-toolbar-filter-badge">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className="catalog-all-toolbar-action"
              onClick={() => setSearchOpen(true)}
              aria-label={t('Search')}
            >
              <img
                className="catalog-all-toolbar-icon"
                src={newCatalogSearchIcon}
                alt=""
              />
            </button>
            <button
              type="button"
              className="catalog-all-toolbar-action"
              onClick={() => !downloading && setExportModalOpen(true)}
              disabled={downloading}
              aria-label={t('Export catalog')}
            >
              <UploadIcon />
            </button>
          </div>
        )}
      </div>
      <ExportOptionModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={downloadCatalogPdf}
        loading={downloading}
      />
    </>
  );
};

export default CatalogProductsToolbarSection;
